import type { BadgeTone } from '../components/ui';

/**
 * The one bill-run `status` -> Badge tone mapping for the SPA.
 *
 * Why this file exists: the same literal map had been hand-copied into six
 * page components (BillRuns, BillRunDetail, Dashboard, BillVerification,
 * BillDetail, and FieldTools/MySubmissionsPage's sibling map). Copies drift,
 * and they did — Dashboard's copy silently omitted `error`, so a *failed*
 * bill run fell through its `?? 'neutral'` fallback and rendered as a grey
 * "no news" chip on the landing screen while BillRuns.tsx rendered the same
 * run red. That is exactly the defect class a shared constant prevents.
 *
 * BillRuns.tsx is the reference implementation the values below are taken
 * from (it is the screen the statuses were designed against, and the only
 * copy that was complete). Hues are unchanged from the pre-Badge hand-rolled
 * classes:
 *   pending          -> neutral   (was gray)
 *   calculating      -> info      (was blue)
 *   pending_approval -> warning   (was yellow)
 *   approved         -> success   (was green)
 *   finalized        -> purple    (unchanged hue)
 *   error            -> danger    (was red)
 *
 * Deliberately NOT covered here, because they are not bill-run statuses:
 *   - a *bill* status (`calculated`/`adjusted`). BillRunDetail.tsx's
 *     getStatusBadge serves both a bill run and a bill, so it spreads this
 *     map and adds those two keys locally; BillDetail.tsx does the same for
 *     the bill-only case.
 *   - the "error whose message starts with `Rejected:`" special case, which
 *     BillRuns.tsx and BillVerification.tsx both render as `orange`. That is
 *     a per-row content check, not a status, so it stays at those call sites.
 *   - BillVerification.tsx's `extraction_status` map
 *     (extracting/extracted/confirmed/error). Its hues were chosen to rhyme
 *     with the ladder above, but it is a council-bill OCR lifecycle over a
 *     different entity, sharing only the key name `error`. Merging it would
 *     let a bill run resolve a tone for `extracted`. It stays local — see
 *     the comment at its definition.
 *
 * Consumers: Dashboard.tsx, BillRuns.tsx (via `billRunStatusTone`),
 * BillRunDetail.tsx (spreads `BILL_RUN_STATUS_TONE`, adds the two bill-level
 * keys). BillDetail.tsx and FieldTools/MySubmissionsPage.tsx still hold local
 * literals and are owned elsewhere.
 */
export const BILL_RUN_STATUS_TONE: Record<string, BadgeTone> = {
  pending: 'neutral',
  calculating: 'info',
  pending_approval: 'warning',
  approved: 'success',
  finalized: 'purple',
  error: 'danger',
};

/**
 * Tone for a bill-run status, falling back to `neutral` for a status this
 * SPA build doesn't know about (the API's status set can gain values ahead of
 * a Pages deploy). Prefer this over indexing the map directly so the fallback
 * is decided in one place rather than re-spelled as `?? 'neutral'` per site.
 */
export function billRunStatusTone(status: string): BadgeTone {
  return BILL_RUN_STATUS_TONE[status] ?? 'neutral';
}
